import {
  Box,
  buildPath,
  buildTemplatePath,
  Button,
  DataArray,
  DataDisplay,
  deepCopy,
  formatDate,
  Grid,
  GridBreak,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Tab,
  TabContext,
  TabList,
  Typography,
  useElementBreakpoints,
  useParams,
  useValidationErrors,
} from "@compass/core";
import {
  FormGroup as MuiFormGroup,
  RadioGroupProps as MuiRadioGroupProps,
  FormControl,
  FormLabel,
  Checkbox,
} from "@mui/material";
import { FormControlLabel } from "@mui/material";
import { DatePicker, Input, Logo } from "components";
import EscalatorWarningIcon from "@mui/icons-material/EscalatorWarning";
import { SignUpSignature } from "pages/students/student-service-templates/StudentServiceTemplateSignUpPage/SignUpSignature";
import {
  Student,
  StudentService,
  StudentServiceTemplateSignUpRequest,
  StudentServiceTemplateSignUpResource,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { thankYouPageRoute } from "pages/ThankYouPage/ThankYouPage";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";

const routeName = "studentServiceSignUp";

class Params {
  tenantName: string = "";
  id: number = 0;
  validationToken: string = "";
}

export type SignUpTab = "Student ID" | "Student Lookup";
const tabs: SignUpTab[] = ["Student ID", "Student Lookup"];

export const studentServiceTemplateSignUpPageRoute: PageRoute<Params, undefined> = {
  handle: "Sign Up for Student Service",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const StudentServiceTemplateSignUpPage = () => {
  const { params } = useParams(routeName, Params);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [request, setRequest] = useState<StudentServiceTemplateSignUpRequest>(
    {} as StudentServiceTemplateSignUpRequest
  );
  const [resource, setResource] = useState<StudentServiceTemplateSignUpResource>({});
  const [studentSignature, setStudentSignature] = useState<any>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [currentTab, setCurrentTab] = useState<SignUpTab>("Student ID");
  const [student, setStudent] = useState<Student | null>();
  const [confirming, setConfirming] = useState<boolean>();
  const [isSignedIn, setIsSignedIn] = useState<boolean>();
  const [canCreatePartial, setCanCreatePartial] = useState<boolean>(false);

  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints } = useElementBreakpoints();
  const properties = resource.properties || [];

  useEffect(() => {
    getRequest();
    fetchResource();
  }, []);

  useEffect(() => {
    console.log("the request", request);
  }, [request]);

  useEffect(() => {
    if (currentTab == "Student Lookup") {
      setRequest(
        produce((draft) => {
          draft.studentId = undefined;
        })
      );
    }
    if (currentTab == "Student ID") {
      setRequest(
        produce((draft) => {
          draft.studentFirstName = undefined;
          draft.studentLastName = undefined;
          draft.studentDOB = undefined;
        })
      );
    }
  }, [currentTab]);

  const getRequest = () => {
    ApiService.studentServiceTemplates
      .getSignUpRequest(params.tenantName, params.id, params.validationToken)
      .then((response) => {
        setRequest(response);
      });
  };

  const fetchResource = () => {
    ApiService.studentServiceTemplates.getSignUpResource(params.tenantName).then((response) => {
      setResource(response);
    });
  };

  const signUpConfirmation = () => {
    var temp = {
      ...request,
      requireSignature: false,
    };

    ApiService.studentServiceTemplates
      .signUpConfirmation(temp)
      .then((response) => {
        let sig = request.requireSignature;
        var r = {
          ...temp,
          requireSignature: sig,
          isSignedIn: response.isSignedIn,
          additionalServiceSpecificIndex: response.additionalServiceSpecificIndex,
        };
        setRequest(r);
        setStudent(response.student);
        setIsSignedIn(response.isSignedIn);
        setConfirming(true);
        setCanCreatePartial(false);
      })
      .catch((ex) => {
        if (ex.response.studentDOB && ex.response.studentLastName) {
          let sig = request.requireSignature;
          var r = {
            ...temp,
            requireSignature: sig,
          };
          setRequest(r);
          setConfirming(true);
          setCanCreatePartial(true);
        } else {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.response);
        }
      });
  };

  const signUp = () => {
    const tempRequest = {
      ...request,
      isPartialStudent: canCreatePartial,
      signatureData:
        typeof studentSignature.isEmpty !== "function" || studentSignature.isEmpty()
          ? null
          : studentSignature.getTrimmedCanvas().toDataURL("image/png"),
    };

    // when already signed in, don't require signature to sign out
    if (request.isSignedIn) {
      tempRequest.requireSignature = false;
    }

    if (tempRequest.studentId == null && student != null) {
      tempRequest.studentId = student.customId1 || student.customId2;
    }

    ApiService.studentServiceTemplates
      .signUp(tempRequest)
      .then((response) => {
        navigate(`../${thankYouPageRoute.getPath()}`);
        dispatch(setAppToast(newToast("success")));

        setTimeout(() => {
          navigate(-1);
        }, 3000);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // onChange(parseInt(event.target.value), event.target.checked);

    var temp = deepCopy(request.additionalServiceSpecificIndex) || [];
    if (event.target.checked) {
      temp.push(parseInt(event.target.value));
    } else {
      var removeIndex = temp.findIndex((t) => t == parseInt(event.target.value));
      temp.splice(removeIndex, 1);
    }
    setRequest(
      produce((draft) => {
        draft.additionalServiceSpecificIndex = temp;
      })
    );
  };

  const goBack = () => {
    setStudent(null);
    setConfirming(false);
    setCanCreatePartial(false);
  };

  return (
    <Page>
      {request.isActive && student && confirming && (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>

          <Typography>Please confirm these details are correct</Typography>

          <Grid container spacing="md">
            <Grid item md={3} xs={12}>
              <DataDisplay label="Name">{student?.fullName}</DataDisplay>
            </Grid>

            <Grid item md={3} xs={12}>
              <DataDisplay label="Date of Birth">{formatDate(student?.dateOfBirth)}</DataDisplay>
            </Grid>

            <GridBreak />

            {/* {request.isForParent && (
              <Grid item xs={12}>
                <Input
                  required
                  label="Number of Parents Per Student"
                  disabled
                  value={request.parentCount}
                  onChange={(e) =>
                    setRequest(
                      produce((draft) => {
                        draft.parentCount = parseFloat(e.target.value);
                      })
                    )
                  }
                />
              </Grid>
            )} */}

            {request.isForParent && (
              <Grid item xs={12}>
                <Input
                  label="Number of Parents Per Student"
                  required
                  value={request.parentCount}
                  onChange={(e) =>
                    setRequest(
                      produce((draft) => {
                        draft.parentCount = parseFloat(e.target.value);
                      })
                    )
                  }
                />
                {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")] && (
                  <Typography color="error">
                    {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")]?.[0]}
                  </Typography>
                )}
              </Grid>
            )}

            {!request.isSignedIn && request.additionalServices && request.additionalServices.length > 0 && (
              <Grid item xs={largeFieldBreakpoints}>
                <FormControl>
                  {<FormLabel id="additional-services">Select Services:</FormLabel>}
                  <MuiFormGroup>
                    {request.additionalServices.map((o, index) => (
                      <Stack key={index} direction="row">
                        <FormControlLabel
                          key={index}
                          value={index}
                          control={
                            <Checkbox
                              checked={request.additionalServiceSpecificIndex?.includes(index)}
                              onChange={handleChange}
                              name={o}
                            />
                          }
                          label={o}
                        />
                      </Stack>
                    ))}
                  </MuiFormGroup>
                </FormControl>

                {validationErrors?.[`additionalServices`] && (
                  <Grid item xs={12}>
                    <Typography color="error">{validationErrors["additionalServices"].toString()}</Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {request.isSignedIn && request.additionalServices && request.additionalServices.length > 0 && (
              <Grid item xs={largeFieldBreakpoints}>
                <FormControl>
                  {<FormLabel id="additional-services">Select Services:</FormLabel>}
                  <MuiFormGroup>
                    {request.additionalServices.map((o, index) => (
                      <Stack key={index} direction="row">
                        <FormControlLabel
                          key={index}
                          value={index}
                          disabled
                          control={
                            <Checkbox
                              checked={request.additionalServiceSpecificIndex?.includes(index)}
                              onChange={handleChange}
                              name={o}
                            />
                          }
                          label={o}
                        />
                      </Stack>
                    ))}
                  </MuiFormGroup>
                </FormControl>
              </Grid>
            )}
          </Grid>

          {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentId")] && (
            <Typography color="error">
              {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentId")]?.[0]}
            </Typography>
          )}

          {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentLastName")] && (
            <Typography color="error">
              {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentLastName")]?.[0]}
            </Typography>
          )}

          {request.requireSignature && !request.isSignedIn && (
            <SignUpSignature
              label="Participant Signature"
              validationErrors={validationErrors}
              signature={studentSignature}
              setSignature={setStudentSignature}
            />
          )}

          <Button onClick={goBack} variant="contained" size="large" fullWidth>
            Back
          </Button>

          <Button
            onClick={signUp}
            variant="contained"
            size="large"
            fullWidth
            disabled={
              !request.studentId && (!request.studentDOB || !request.studentFirstName || !request.studentLastName)
            }
          >
            {isSignedIn ? "Check Out" : "Confirm"}
          </Button>
        </Stack>
      )}
      {request.isActive && canCreatePartial && confirming && (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>

          <Typography>Student not found, would you like to continue anyway?</Typography>

          <Grid container spacing="md">
            <Grid item md={3} xs={12}>
              <DataDisplay label="Name">
                {request?.studentFirstName} {request?.studentLastName}
              </DataDisplay>
            </Grid>

            <Grid item md={3} xs={12}>
              <DataDisplay label="Date of Birth">{formatDate(request?.studentDOB)}</DataDisplay>
            </Grid>

            <GridBreak />

            {request.isForParent && (
              <Grid item xs={12}>
                <Input
                  label="Number of Parents Per Student"
                  required
                  value={request.parentCount}
                  onChange={(e) =>
                    setRequest(
                      produce((draft) => {
                        draft.parentCount = parseFloat(e.target.value);
                      })
                    )
                  }
                />
                {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")] && (
                  <Typography color="error">
                    {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")]?.[0]}
                  </Typography>
                )}
              </Grid>
            )}

            {request.requireSignature && !request.isSignedIn && (
              <Grid item xs={12}>
                <Stack spacing="lg" alignItems={"center"}>
                  <SignUpSignature
                    label="Participant Signature"
                    validationErrors={validationErrors}
                    signature={studentSignature}
                    setSignature={setStudentSignature}
                  />
                </Stack>
              </Grid>
            )}

            {!request.isSignedIn && request.additionalServices && request.additionalServices.length > 0 && (
              <Grid item xs={largeFieldBreakpoints}>
                <FormControl>
                  {<FormLabel id="additional-services">Select Services:</FormLabel>}
                  <MuiFormGroup>
                    {request.additionalServices.map((o, index) => (
                      <Stack key={index} direction="row">
                        <FormControlLabel
                          key={index}
                          value={index}
                          control={
                            <Checkbox
                              checked={request.additionalServiceSpecificIndex?.includes(index)}
                              onChange={handleChange}
                              name={o}
                            />
                          }
                          label={o}
                        />
                      </Stack>
                    ))}
                  </MuiFormGroup>
                </FormControl>

                {validationErrors?.[`additionalServices`] && (
                  <Grid item xs={12}>
                    <Typography color="error">{validationErrors["additionalServices"].toString()}</Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>

          {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentId")] && (
            <Typography color="error">
              {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentId")]?.[0]}
            </Typography>
          )}

          {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentLastName")] && (
            <Typography color="error">
              {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentLastName")]?.[0]}
            </Typography>
          )}

          <Button onClick={goBack} variant="contained" size="large" fullWidth>
            Back
          </Button>

          <Button
            onClick={signUp}
            variant="contained"
            size="large"
            fullWidth
            disabled={
              !request.studentId && (!request.studentDOB || !request.studentLastName || !request.studentFirstName)
            }
          >
            {isSignedIn ? "Check Out" : "Confirm"}
          </Button>
        </Stack>
      )}
      {request.isActive && (!student || !confirming) && !canCreatePartial && (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>

          <Grid ref={breakpointsRef} container spacing="md">
            <Grid item xs={smallFieldBreakpoints}>
              <DataDisplay
                label={
                  properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("date"))
                    ?.displayName
                }
              >
                {formatDate(request?.date)}
              </DataDisplay>
            </Grid>

            <Grid item xs={smallFieldBreakpoints}>
              <DataDisplay
                label={
                  properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("serviceName"))
                    ?.displayName
                }
              >
                {request?.serviceName}
              </DataDisplay>
            </Grid>

            <Grid item xs={smallFieldBreakpoints}>
              <DataDisplay
                label={
                  properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("serviceType"))
                    ?.displayName
                }
              >
                {request?.serviceType}
                {request.isForParent && <EscalatorWarningIcon style={{ marginLeft: "5px" }} />}
              </DataDisplay>
            </Grid>

            {request?.durationInMinutes && (
              <Grid item xs={smallFieldBreakpoints}>
                <DataDisplay
                  label={
                    properties.find(
                      (x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("durationInMinutes")
                    )?.displayName
                  }
                >
                  {request?.durationInMinutes} {request?.durationInMinutes != null ? "minutes" : "N/A"}
                </DataDisplay>
              </Grid>
            )}

            <Grid item xs={largeFieldBreakpoints}>
              <DataDisplay
                label={
                  properties.find((x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("providers"))
                    ?.displayName
                }
              >
                <DataArray value={request?.providers} />
              </DataDisplay>
            </Grid>

            <GridBreak />

            {/* {request.isForParent && (
              <Grid item md={3} xs={12}>
                <Input
                  label="Number of Parents Per Student"
                  required
                  value={request.parentCount}
                  onChange={(e) =>
                    setRequest(
                      produce((draft) => {
                        draft.parentCount = parseFloat(e.target.value);
                      })
                    )
                  }
                />
                {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")] && (
                  <Typography color="error">
                    {validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("parentCount")]?.[0]}
                  </Typography>
                )}
              </Grid>
            )} */}
          </Grid>

          <TabContext value={currentTab}>
            <TabList
              onChange={(e, value) => setCurrentTab(value)}
              variant="standard"
              scrollButtons
              allowScrollButtonsMobile
            >
              {tabs.map((tab) => (
                <Tab
                  key={tab}
                  label={
                    tab == "Student ID" && resource.customIdTypes && resource.customIdTypes.length > 0
                      ? resource.customIdTypes[0].name
                      : tab
                  }
                  value={tab}
                />
              ))}
            </TabList>
          </TabContext>

          {currentTab == "Student ID" && (
            <Input
              label={
                resource.customIdTypes && resource.customIdTypes.length > 0
                  ? resource.customIdTypes[0].name
                  : "Student ID"
              }
              errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentId")]}
              value={request?.studentId}
              onChange={(e) =>
                setRequest(
                  produce((draft) => {
                    draft.studentId = e.target.value;
                  })
                )
              }
            />
          )}

          {currentTab == "Student Lookup" && (
            <Grid ref={breakpointsRef} container spacing="md">
              <Grid item md={6} xs={12}>
                <Input
                  label="First Name"
                  errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentFirstName")]}
                  value={request?.studentFirstName}
                  onChange={(e) =>
                    setRequest(
                      produce((draft) => {
                        draft.studentFirstName = e.target.value;
                      })
                    )
                  }
                />
              </Grid>

              <Grid item md={6} xs={12}>
                <Input
                  label="Last Name"
                  value={request?.studentLastName}
                  errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentLastName")]}
                  onChange={(e) =>
                    setRequest(
                      produce((draft) => {
                        draft.studentLastName = e.target.value;
                      })
                    )
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <DatePicker
                  label="Date of Birth"
                  modelProperty={properties.find(
                    (x) => x.propertyName === pn<StudentServiceTemplateSignUpRequest>("studentDOB")
                  )}
                  errors={validationErrors?.[pn<StudentServiceTemplateSignUpRequest>("studentDOB")]}
                  value={request?.studentDOB}
                  onChange={(value) =>
                    setRequest(
                      produce((draft) => {
                        draft.studentDOB = value;
                      })
                    )
                  }
                />
              </Grid>
            </Grid>
          )}

          <Button
            onClick={signUpConfirmation}
            variant="contained"
            size="large"
            fullWidth
            disabled={
              !request.studentId && (!request.studentDOB || !request.studentFirstName || !request.studentLastName)
            }
          >
            Next
          </Button>
        </Stack>
      )}
      {!request.isActive && (
        <Stack spacing="lg" alignItems={"center"}>
          <Box>
            <Logo width="100px" animate />
          </Box>

          <Typography>This link is no longer active.</Typography>
        </Stack>
      )}

      {/* <Box>{tenantName}</Box>
      <Box>{id}</Box>
      <Box>{validationToken}</Box> */}
    </Page>
  );
};
